import React, { useState } from 'react';
import { SignIn, SignUp } from '@clerk/clerk-react';

const LoginScreen = ({ onLogin, clerkUnavailable = false, onClose }) => {
  const [isSignUp, setIsSignUp] = useState(false);

  const handleAuthComplete = () => {
    if (typeof onLogin === 'function') {
      onLogin();
    }
  };

  // Custom Clerk appearance to match LinePicks theme
  const clerkAppearance = {
    baseTheme: undefined, // Remove default theme
    elements: {
      rootBox: {
        width: '100%',
        maxWidth: '420px',
        margin: '0 auto',
      },
      card: {
        background: 'rgba(8, 14, 26, 0.95)',
        backdropFilter: 'blur(20px)',
        borderRadius: '20px',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
        padding: '32px',
      },
      cardBox: {
        background: 'transparent',
      },
      cardBox: {
        background: 'transparent',
      },
      header: {
        display: 'none', // Hide default header to replace with logo
      },
      headerTitle: {
        display: 'none', // Hide "Sign in to linepicks" text
      },
      headerSubtitle: {
        display: 'none',
      },
      socialButtonsBlockButton: {
        background: 'rgba(255, 255, 255, 0.05)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        color: '#ffffff',
        borderRadius: '8px',
        '&:hover': {
          background: 'rgba(255, 255, 255, 0.1)',
          borderColor: 'rgba(255, 117, 31, 0.5)',
        },
      },
      formButtonPrimary: {
        background: 'linear-gradient(135deg, rgba(255, 117, 31, 0.9) 0%, rgba(255, 150, 80, 0.9) 100%)',
        borderRadius: '8px',
        border: '1px solid rgba(255, 117, 31, 0.3)',
        color: '#ffffff',
        fontWeight: '600',
        fontSize: '0.95rem',
        padding: '12px 24px',
        '&:hover': {
          background: 'linear-gradient(135deg, rgba(255, 117, 31, 1) 0%, rgba(255, 150, 80, 1) 100%)',
          transform: 'translateY(-1px)',
          boxShadow: '0 4px 12px rgba(255, 117, 31, 0.4)',
        },
      },
      formFieldInput: {
        background: 'rgba(255, 255, 255, 0.05)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        borderRadius: '8px',
        color: '#ffffff',
        padding: '12px 16px',
        '&:focus': {
          borderColor: 'rgba(255, 117, 31, 0.5)',
          boxShadow: '0 0 0 3px rgba(255, 117, 31, 0.1)',
        },
      },
      formFieldLabel: {
        color: 'rgba(255, 255, 255, 0.8)',
        fontSize: '0.875rem',
        fontWeight: '500',
      },
      footerActionLink: {
        color: 'rgba(255, 117, 31, 0.9)',
        '&:hover': {
          color: 'rgba(255, 150, 80, 1)',
        },
      },
      identityPreviewText: {
        color: 'rgba(255, 255, 255, 0.7)',
      },
      formResendCodeLink: {
        color: 'rgba(255, 117, 31, 0.9)',
      },
      dividerLine: {
        background: 'rgba(255, 255, 255, 0.1)',
      },
      dividerText: {
        color: 'rgba(255, 255, 255, 0.5)',
      },
      otpCodeFieldInput: {
        border: '1px solid #ffffff',
        color: '#ffffff',
        background: 'rgba(255, 255, 255, 0.05)',
      },
      footer: {
        display: 'none', // Hide default footer
      },
      footerPages: {
        display: 'none', // Hide footer pages
      },
    },
    layout: {
      socialButtonsPlacement: 'top',
      socialButtonsVariant: 'blockButton',
    },
  };

  if (clerkUnavailable) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        background: '#000000',
        padding: '20px'
      }}>
        <div style={{
          background: 'rgba(8, 14, 26, 0.95)',
          borderRadius: '20px',
          padding: '32px',
          maxWidth: '420px',
          width: '100%',
          textAlign: 'center',
          color: 'rgba(255, 255, 255, 0.7)'
        }}>
          <p>Authentication is currently unavailable. Please configure Clerk to enable sign in.</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{`
        /* Hide any duplicate Clerk UI elements */
        .cl-rootBox > div:not(:first-child),
        .cl-cardBox > div:not(:first-child),
        .cl-main > div:last-child:not(.cl-form),
        [class*="cl-footer"]:not(:first-of-type),
        .cl-card + .cl-card,
        .cl-rootBox + .cl-rootBox {
          display: none !important;
        }
        /* Hide Clerk's default footer if it appears */
        .cl-footer,
        .cl-footerPages {
          display: none !important;
        }
        /* Push form content down to make room for logo and buttons */
        .cl-main {
          padding-top: 140px !important;
        }
        /* Ensure card is centered */
        .cl-rootBox {
          display: flex !important;
          justify-content: center !important;
          align-items: center !important;
        }
        /* Remove any black backgrounds from Clerk components */
        .cl-cardBox {
          background: transparent !important;
        }
        .cl-rootBox {
          background: transparent !important;
        }
        /* Verification code input boxes - white outline and white text */
        .cl-otpCodeFieldInput,
        input[type="text"][inputmode="numeric"],
        .cl-codeInput,
        input.cl-codeInput {
          border: 1px solid #ffffff !important;
          color: #ffffff !important;
          background: rgba(255, 255, 255, 0.05) !important;
        }
        .cl-otpCodeFieldInput:focus,
        input[type="text"][inputmode="numeric"]:focus,
        .cl-codeInput:focus {
          border-color: #ffffff !important;
          color: #ffffff !important;
        }
      `}</style>
      <div style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        background: 'transparent',
        padding: '20px'
      }}>
        {onClose && (
          <button 
            onClick={onClose}
            style={{
              position: 'fixed',
              top: '20px',
              right: '20px',
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              background: 'rgba(255, 255, 255, 0.1)',
              color: '#ffffff',
              fontSize: '1.5rem',
              cursor: 'pointer',
              zIndex: 1000
            }}
          >
            ×
          </button>
        )}
        <div style={{ 
          position: 'relative', 
          width: '100%', 
          maxWidth: '420px',
          margin: '0 auto'
        }}>
          {isSignUp ? (
            <SignUp
              routing="virtual"
              signInUrl="#"
              afterSignUp={handleAuthComplete}
              appearance={clerkAppearance}
            />
          ) : (
            <SignIn
              routing="virtual"
              signUpUrl="#"
              afterSignIn={handleAuthComplete}
              appearance={clerkAppearance}
            />
          )}
          {/* Custom logo and toggle buttons inside the card */}
          <div style={{
            position: 'absolute',
            top: '32px',
            left: '32px',
            right: '32px',
            zIndex: 10,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '20px'
          }}>
            <img 
              src="/logos/longLogo.png" 
              alt="LinePicks" 
              style={{
                height: 'auto',
                maxWidth: '180px',
                width: 'auto',
                marginBottom: '8px'
              }}
            />
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => setIsSignUp(false)}
                style={{
                  padding: '8px 16px',
                  borderRadius: '8px',
                  border: `1px solid ${isSignUp ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 117, 31, 0.5)'}`,
                  background: isSignUp ? 'rgba(255, 255, 255, 0.05)' : 'rgba(255, 117, 31, 0.15)',
                  color: isSignUp ? 'rgba(255, 255, 255, 0.7)' : '#ffb07a',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  transition: 'all 0.2s ease',
                }}
              >
                Sign In
              </button>
              <button
                onClick={() => setIsSignUp(true)}
                style={{
                  padding: '8px 16px',
                  borderRadius: '8px',
                  border: `1px solid ${!isSignUp ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 117, 31, 0.5)'}`,
                  background: !isSignUp ? 'rgba(255, 255, 255, 0.05)' : 'rgba(255, 117, 31, 0.15)',
                  color: !isSignUp ? 'rgba(255, 255, 255, 0.7)' : '#ffb07a',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  transition: 'all 0.2s ease',
                }}
              >
                Sign Up
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default LoginScreen;
